import type { DataScope, PermCode, PermissionSnapshot } from './types';

export type PermMatchMode = 'all' | 'any';

/** 数据范围提示文案（列表页顶部展示，DR-8.5）。 */
export const DATA_SCOPE_LABEL: Record<DataScope, string> = {
  SELF: '当前数据范围：仅本人',
  DEPT: '当前数据范围：本科室',
  ORG: '当前数据范围：本院',
  SUPERVISED_ORG: '当前数据范围：本院 + 下属单位',
  ALL: '当前数据范围：行政区域（全域）'
};

/**
 * 权限判定：`required` 为空视为不限制；`granted` 含 `*` 视为全部放行。
 * 前端只做“看不见”，最终以后端拦截为准。
 */
export function hasPermission(
  granted: Iterable<PermCode> | null | undefined,
  required?: PermCode | readonly PermCode[] | null,
  mode: PermMatchMode = 'all'
): boolean {
  const requiredList = toRequiredList(required);
  if (requiredList.length === 0) return true;
  if (!granted) return false;

  const owned = granted instanceof Set ? granted : new Set(granted);
  if (owned.has('*')) return true;
  return mode === 'any'
    ? requiredList.some((code) => owned.has(code))
    : requiredList.every((code) => owned.has(code));
}

function toRequiredList(required?: PermCode | readonly PermCode[] | null): readonly PermCode[] {
  if (!required) return [];
  if (typeof required === 'string') return required ? [required] : [];
  return required.filter((code) => !!code);
}

/** 跨机构可见需同时满足：范围含下属/全区 + 等级允许跨机构（DR-8.7）。 */
export function canCrossOrg(scope: DataScope, crossOrgAllowed: boolean): boolean {
  return crossOrgAllowed && (scope === 'SUPERVISED_ORG' || scope === 'ALL');
}

export interface PermissionChecker {
  has(required?: PermCode | readonly PermCode[] | null, mode?: PermMatchMode): boolean;
  canCrossOrg(): boolean;
  readonly dataScope: DataScope;
  readonly dataScopeLabel: string;
}

export function createPermissionChecker(snapshot: PermissionSnapshot): PermissionChecker {
  const granted = new Set(snapshot.codes);
  return {
    has: (required, mode) => hasPermission(granted, required, mode),
    canCrossOrg: () => canCrossOrg(snapshot.dataScope, snapshot.crossOrgAllowed ?? false),
    dataScope: snapshot.dataScope,
    dataScopeLabel: DATA_SCOPE_LABEL[snapshot.dataScope]
  };
}
